import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { primaryButtonSx, pageTabsSx } from '../../styles/adminPageStyles';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Pagination,
  Tooltip,
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  CallReceived as IncomingIcon,
  CallMade as OutgoingIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import PageHeader from '../../components/PageHeader';
import CreateDeliveryCheckModal from './CreateDeliveryCheckModal';
import DeliveryCheckActionsMenu from './DeliveryCheckActionsMenu';
import { resolveAdminUiLabel } from '../../constants/adminUiLabels';
import { useAdminUiLabels } from '../../context/AdminUiLabelsContext';

interface DeliveryTransfer {
  transfer_id: string;
  asset_id: string;
  asset_name: string;
  serial_number: string;
  asset_type: string;
  from_business_id: string;
  from_business_name: string;
  to_business_id: string;
  to_business_name: string;
  transferred_by_name: string;
  transfer_status: 'pending' | 'in_transit' | 'delivered' | 'cancelled';
  delivery_check_id: string | null;
  delivery_check_name: string | null;
  delivery_check_status: number | null;
  created_at: string;
  completed_at: string | null;
  delivery_completed_at: string | null;
  delivery_completed_by_name: string | null;
  delivery_responses_count: number;
  notes: string | null;
  /** Tracks whether this row came from asset_transfers or outgoing_delivery_requests */
  delivery_source: 'asset_transfer' | 'outgoing_request';
}

interface DeliveryStats {
  total_deliveries: number;
  incoming_count: number;
  outgoing_count: number;
  pending_count: number;
  in_transit_count: number;
  delivered_count: number;
}

/** Manual temp-asset checks live in outgoing_delivery_requests; direction drives tab placement. */
const resolveManualDeliveryDirection = (row: {
  delivery_direction?: string | null;
  check_name?: string | null;
}): 'incoming' | 'outgoing' => {
  if (row.delivery_direction === 'incoming' || row.delivery_direction === 'outgoing') {
    return row.delivery_direction;
  }
  const name = (row.check_name || '').toLowerCase();
  if (name.startsWith('incoming delivery')) return 'incoming';
  return 'outgoing';
};

// Subtle check ID display with copy — for support/debugging
const CheckIdCopy: React.FC<{ checkId: string; label?: string }> = ({ checkId, label = 'Check ID' }) => {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(checkId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };
  if (!checkId) return null;
  return (
    <Tooltip title={copied ? 'Copied!' : `Copy ${label} for support`} arrow placement="top">
      <Box
        component="span"
        onClick={handleCopy}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          fontSize: '0.7rem',
          fontFamily: 'monospace',
          color: 'text.disabled',
          cursor: 'pointer',
          '&:hover': { color: 'text.secondary' },
        }}
      >
        <ContentCopyIcon sx={{ fontSize: 12, opacity: 0.7 }} />
        <Box component="span" sx={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>{checkId}</Box>
      </Box>
    </Tooltip>
  );
};

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} id={`delivery-tabpanel-${index}`} aria-labelledby={`delivery-tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

// Dashboard-style stat card (rounded icon box, hover lift)
const StatCard: React.FC<{
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}> = ({ title, value, icon, color }) => {
  const theme = useTheme();
  const paletteColor = theme.palette[color];
  const colorValue = typeof paletteColor === 'object' && paletteColor && 'main' in paletteColor ? paletteColor.main : theme.palette.primary.main;
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: 4,
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 45px rgba(0,0,0,0.12)',
        },
      }}
    >
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, '&:last-child': { pb: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '16px',
              backgroundColor: `${colorValue}22`,
              color: colorValue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { fontSize: 'large' })}
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary">{title}</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const DELIVERY_CHECK_TABS = ['incoming', 'outgoing'] as const;

/** Normalize response_text whether the API returned a string or an already-parsed object. */
const normalizeResponseText = (raw: unknown): string | null => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    try {
      return JSON.stringify(raw);
    } catch {
      return null;
    }
  }
  return String(raw);
};

/** Parse scribble signature JSON: { paths: string[], width?, height? }. */
const parseScribbleSignature = (raw: unknown): { paths: string[]; width: number; height: number } | null => {
  let parsed: any = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{') || !trimmed.includes('"paths"')) return null;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.paths) || parsed.paths.length === 0) {
    return null;
  }
  return {
    paths: parsed.paths.map(String),
    width: Number(parsed.width) || 300,
    height: Number(parsed.height) || 150,
  };
};

const isImageDataAnswer = (text?: string | null): boolean => {
  if (!text) return false;
  const trimmed = text.trim();
  if (trimmed.startsWith('data:image/')) return true;
  // Avoid treating scribble JSON as base64
  if (trimmed.startsWith('{')) return false;
  return trimmed.length > 200 && /^[A-Za-z0-9+/=\s]+$/.test(trimmed);
};

/** Compute SVG viewBox that fits all path points (paths often go negative outside the pad). */
const scribbleViewBox = (paths: string[], fallbackW: number, fallbackH: number): string => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const numRe = /-?\d+(?:\.\d+)?/g;
  for (const d of paths) {
    const nums = d.match(numRe);
    if (!nums) continue;
    for (let i = 0; i + 1 < nums.length; i += 2) {
      const x = parseFloat(nums[i]);
      const y = parseFloat(nums[i + 1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  if (!Number.isFinite(minX)) {
    return `0 0 ${fallbackW} ${fallbackH}`;
  }
  const pad = 8;
  return `${minX - pad} ${minY - pad} ${Math.max(maxX - minX, 1) + pad * 2} ${Math.max(maxY - minY, 1) + pad * 2}`;
};

const renderScribbleSignatureSvg = (sig: { paths: string[]; width: number; height: number }) => {
  const displayW = Math.min(sig.width, 320);
  const displayH = Math.min(Math.max(sig.height * (displayW / sig.width), 80), 180);
  return (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, bgcolor: '#fff', display: 'inline-block', maxWidth: '100%' }}>
      <svg
        width={displayW}
        height={displayH}
        viewBox={scribbleViewBox(sig.paths, sig.width, sig.height)}
        style={{ display: 'block', maxWidth: '100%' }}
      >
        {sig.paths.map((d, i) => (
          <path
            key={i}
            d={d}
            stroke="#111"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
    </Box>
  );
};

// Renders a delivery check response answer, special-casing signature/image
// payloads so they show up as an actual signature/image rather than raw
// coordinate or base64 text.
const renderDeliveryResponseAnswer = (item: any) => {
  const raw = item.response?.response_text;
  const text = normalizeResponseText(raw);
  const questionType = (item.question?.question_type || '').toLowerCase();
  const questionLabel = (item.question?.question_text || '').toLowerCase();
  const looksLikeSignatureQ =
    questionType === 'signature' ||
    questionType === 'scribble_signature' ||
    questionLabel.includes('signature');

  if (!text && !raw) {
    return <Typography color="text.secondary">No answer provided</Typography>;
  }

  const scribble = parseScribbleSignature(raw ?? text);
  if (scribble) {
    return renderScribbleSignatureSvg(scribble);
  }

  if (text && isImageDataAnswer(text)) {
    const src = text.trim().startsWith('data:') ? text.trim() : `data:image/png;base64,${text.trim()}`;
    return (
      <img
        src={src}
        alt={looksLikeSignatureQ ? 'Signature' : 'Response image'}
        style={{ maxWidth: '250px', maxHeight: '150px', borderRadius: 8, border: '1px solid #e0e0e0', objectFit: 'contain' }}
      />
    );
  }

  // Signature question with unrecognized payload — never dump raw coordinates as text
  if (looksLikeSignatureQ) {
    return (
      <Typography color="text.secondary" variant="body2">
        Signature captured (preview unavailable)
      </Typography>
    );
  }

  return <Typography sx={{ wordBreak: 'break-word' }}>{text}</Typography>;
};

const DeliveryChecks: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminUiLabels } = useAdminUiLabels();
  const requestIdParam = searchParams.get('request_id') || searchParams.get('focus');
  const deepLinkHandled = useRef(false);

  useEffect(() => {
    deepLinkHandled.current = false;
  }, [requestIdParam]);

  const [tab, setTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('request_id') || params.get('focus')) {
      const t = params.get('tab');
      const idx = DELIVERY_CHECK_TABS.indexOf(t as (typeof DELIVERY_CHECK_TABS)[number]);
      return idx >= 0 ? idx : 1;
    }
    const t = params.get('tab');
    const idx = DELIVERY_CHECK_TABS.indexOf(t as (typeof DELIVERY_CHECK_TABS)[number]);
    return idx >= 0 ? idx : 0;
  });

  useEffect(() => {
    if (requestIdParam) {
      const t = searchParams.get('tab');
      const idx = DELIVERY_CHECK_TABS.indexOf(t as (typeof DELIVERY_CHECK_TABS)[number]);
      setTab(idx >= 0 ? idx : 1);
      return;
    }
    const t = searchParams.get('tab');
    if (!t) return;
    const idx = DELIVERY_CHECK_TABS.indexOf(t as (typeof DELIVERY_CHECK_TABS)[number]);
    if (idx >= 0) setTab(idx);
  }, [searchParams, requestIdParam]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
    if (newValue === 0) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: DELIVERY_CHECK_TABS[newValue] }, { replace: true });
    }
  };
  const [stats, setStats] = useState<DeliveryStats | null>(null);
  const [incomingDeliveries, setIncomingDeliveries] = useState<DeliveryTransfer[]>([]);
  const [outgoingDeliveries, setOutgoingDeliveries] = useState<DeliveryTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryTransfer | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [responsesDialogOpen, setResponsesDialogOpen] = useState(false);
  const [responses, setResponses] = useState<any[]>([]);
  const [responsesExternalDriver, setResponsesExternalDriver] = useState<{
    name: string | null;
    email: string | null;
    completedViaLink: boolean;
    dvlaCheck?: {
      requestId: string;
      status: string;
      licenceNumber: string | null;
      completedAt: string | null;
      licenceValid: boolean | null;
      checkStatus: string | null;
    } | null;
  } | null>(null);
  const [loadingResponses, setLoadingResponses] = useState(false);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [checkStatusFilter, setCheckStatusFilter] = useState<string>('all');

  // Pagination (per tab; reset when tab or filters change)
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [deliveryRowsPerPage, setDeliveryRowsPerPage] = useState(25);

  // New delivery check modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const fetchDeliveryData = async (options?: { background?: boolean }) => {
    try {
      if (options?.background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const token = localStorage.getItem('token');
      const businessId = localStorage.getItem('businessId');

      if (!token || !businessId) {
        setError('Authentication token or business ID not found');
        return;
      }

      // Fetch delivery transfers for this business using the history endpoint
      const [historyResponse, outgoingResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/deliveries/history`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            limit: 1000, // Get all recent transfers
          },
        }).catch(err => {
          console.error('Error fetching delivery history:', err);
          return { data: { success: true, transfers: [] } };
        }),
        // Fetch outgoing delivery requests (all statuses)
        axios.get(`${API_BASE_URL}/outgoing-deliveries/pending`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            status: 'pending,in_progress,completed,rejected', // Get all statuses
          },
        }).catch(err => {
          console.error('Error fetching outgoing deliveries:', err);
          return { data: { success: true, deliveries: [] } };
        }),
      ]);

      const deliveries: DeliveryTransfer[] = [];

      // Process standard delivery transfers (from asset_transfers)
      if (historyResponse.data.success) {
        const transfers = historyResponse.data.transfers || [];
        
        // Map API response fields to our component's expected format
        const standardDeliveries = transfers.map((t: any) => ({
          transfer_id: t.transferId,
          asset_id: t.assetId,
          asset_name: t.assetName,
          serial_number: t.serialNumber,
          asset_type: t.assetType,
          from_business_id: t.fromBusinessId,
          from_business_name: t.fromBusinessName,
          to_business_id: t.toBusinessId,
          to_business_name: t.toBusinessName,
          transferred_by_name: t.transferredBy,
          transfer_status: t.status,
          delivery_check_id: t.deliveryCheckId,
          delivery_check_name: t.deliveryCheckName,
          delivery_check_status: t.deliveryCheckStatus,
          created_at: t.initiatedAt,
          completed_at: t.completedAt,
          delivery_completed_at: t.completedAt,
          delivery_completed_by_name: null,
          delivery_responses_count: t.deliveryCheckStatus === 100 ? 1 : 0,
          notes: t.notes,
          delivery_source: 'asset_transfer' as const,
        }));
        
        deliveries.push(...standardDeliveries);
      }

      // Process outgoing delivery requests (from outgoing_delivery_requests)
      if (outgoingResponse.data.success) {
        const outgoingDeliveries = outgoingResponse.data.deliveries || [];
        
        // Filter to only include deliveries with delivery checks (check_id)
        const deliveriesWithChecks = outgoingDeliveries.filter((od: any) => od.check_id);
        
        // Map outgoing delivery requests to the same format
        const mappedOutgoing = deliveriesWithChecks.map((od: any) => {
          // Map request_status to transfer_status
          let transferStatus: 'pending' | 'in_transit' | 'delivered' | 'cancelled' = 'pending';
          if (od.request_status === 'completed') {
            transferStatus = 'delivered';
          } else if (od.request_status === 'in_progress') {
            transferStatus = 'in_transit';
          } else if (od.request_status === 'rejected') {
            transferStatus = 'cancelled';
          }

          const manualDirection = resolveManualDeliveryDirection(od);
          const isManualIncoming = manualDirection === 'incoming';

          return {
            transfer_id: od.request_id, // Use request_id as transfer_id
            asset_id: od.asset_id || od.temporary_asset_id,
            asset_name: od.asset_name || od.temporary_asset_specification
              || (od.whole_good_number ? `WGS${od.whole_good_number_prefix && od.whole_good_number_prefix !== 'WGS' ? od.whole_good_number_prefix : ''}${od.whole_good_number}` : 'Temporary asset'),
            serial_number: od.serial_number || od.temporary_asset_serial_number || null,
            asset_type: null, // Outgoing deliveries may not have asset_type
            from_business_id: isManualIncoming ? null : businessId,
            from_business_name: null,
            to_business_id: isManualIncoming ? businessId : null,
            to_business_name: null,
            transferred_by_name: od.created_by_first_name && od.created_by_last_name 
              ? `${od.created_by_first_name} ${od.created_by_last_name}` 
              : null,
            transfer_status: transferStatus,
            delivery_check_id: od.check_id,
            delivery_check_name: od.check_name,
            delivery_check_status: od.check_status,
            created_at: od.created_at || od.received_at,
            completed_at: od.updated_at,
            delivery_completed_at: od.request_status === 'completed' ? od.updated_at : null,
            delivery_completed_by_name: null,
            delivery_responses_count: od.check_status === 100 ? 1 : 0,
            notes: null,
            delivery_source: 'outgoing_request' as const,
          };
        });
        
        deliveries.push(...mappedOutgoing);
      }
        
      // Separate into incoming and outgoing
      // Sort by created_at (newest first)
      const sortedDeliveries = deliveries.sort((a, b) => {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return dateB - dateA; // Newest first
      });

      const incoming = sortedDeliveries.filter((d: DeliveryTransfer) => d.to_business_id === businessId);
      const outgoing = sortedDeliveries.filter((d: DeliveryTransfer) => d.from_business_id === businessId);

      setIncomingDeliveries(incoming);
      setOutgoingDeliveries(outgoing);

      // Calculate stats
      const statsData: DeliveryStats = {
        total_deliveries: deliveries.length,
        incoming_count: incoming.length,
        outgoing_count: outgoing.length,
        pending_count: deliveries.filter((d: DeliveryTransfer) => d.transfer_status === 'pending').length,
        in_transit_count: deliveries.filter((d: DeliveryTransfer) => d.transfer_status === 'in_transit').length,
        delivered_count: deliveries.filter((d: DeliveryTransfer) => d.transfer_status === 'delivered').length,
      };
      setStats(statsData);
    } catch (err: any) {
      console.error('Error fetching delivery data:', err);
      setError(err.response?.data?.error || 'Failed to load delivery data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDeliveryData();
  }, []);

  // Reset to first page when tab or filters change
  useEffect(() => {
    setDeliveryPage(1);
  }, [tab, searchQuery, statusFilter, checkStatusFilter]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDeliveryData();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'in_transit':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'delivered':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_transit':
        return <PendingIcon fontSize="small" />;
      case 'pending':
        return <PendingIcon fontSize="small" />;
      case 'cancelled':
        return <CancelIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const isDeliveryCheckCompleted = (delivery: DeliveryTransfer): boolean => {
    if (delivery.delivery_completed_at) return true;
    if (delivery.delivery_check_status === 100) return true;
    if (delivery.transfer_status === 'delivered' && delivery.delivery_responses_count > 0) return true;
    return false;
  };

  const handleViewDetails = (delivery: DeliveryTransfer) => {
    setSelectedDelivery(delivery);
    setDetailsDialogOpen(true);
  };

  const handleViewResponses = async (delivery: DeliveryTransfer) => {
    if (!delivery.delivery_check_id) {
      return;
    }

    try {
      setLoadingResponses(true);
      setResponses([]);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/deliveries/${delivery.delivery_check_id}/responses`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setResponses(response.data.responses || []);
        setResponsesExternalDriver(response.data.externalDriver || null);
        setSelectedDelivery(delivery);
        setResponsesDialogOpen(true);
      } else {
        setError(response.data.error || 'Failed to load responses');
      }
    } catch (err: any) {
      console.error('Error fetching responses:', err);
      setError(err.response?.data?.error || 'Failed to load responses');
    } finally {
      setLoadingResponses(false);
    }
  };

  // Deep link from calendar: /admin/delivery-checks?tab=outgoing&request_id=…
  useEffect(() => {
    if (!requestIdParam || loading || deepLinkHandled.current) return;
    const delivery =
      outgoingDeliveries.find((d) => d.transfer_id === requestIdParam) ??
      incomingDeliveries.find((d) => d.transfer_id === requestIdParam);
    if (!delivery?.delivery_check_id) return;
    deepLinkHandled.current = true;
    if (delivery.from_business_id) setTab(1);
    else if (delivery.to_business_id) setTab(0);
    void handleViewResponses(delivery);
  }, [requestIdParam, loading, outgoingDeliveries, incomingDeliveries]);

  // Filter and search deliveries
  const filterAndSearchDeliveries = (deliveries: DeliveryTransfer[]): DeliveryTransfer[] => {
    let filtered = [...deliveries];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((d) => {
        const assetName = (d.asset_name || '').toLowerCase();
        const serialNumber = (d.serial_number || '').toLowerCase();
        const fromBusiness = (d.from_business_name || '').toLowerCase();
        const toBusiness = (d.to_business_name || '').toLowerCase();
        const checkName = (d.delivery_check_name || '').toLowerCase();
        const transferredBy = (d.transferred_by_name || '').toLowerCase();
        
        return (
          assetName.includes(query) ||
          serialNumber.includes(query) ||
          fromBusiness.includes(query) ||
          toBusiness.includes(query) ||
          checkName.includes(query) ||
          transferredBy.includes(query)
        );
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((d) => d.transfer_status === statusFilter);
    }

    // Check status filter
    if (checkStatusFilter !== 'all') {
      if (checkStatusFilter === 'completed') {
        filtered = filtered.filter((d) => isDeliveryCheckCompleted(d));
      } else if (checkStatusFilter === 'pending') {
        filtered = filtered.filter((d) => !isDeliveryCheckCompleted(d) && d.delivery_check_id);
      } else if (checkStatusFilter === 'no_check') {
        filtered = filtered.filter((d) => !d.delivery_check_id);
      }
    }

    return filtered;
  };

  const renderDeliveryTable = (deliveries: DeliveryTransfer[], isIncoming: boolean) => {
    const filteredDeliveries = filterAndSearchDeliveries(deliveries);
    const totalCount = filteredDeliveries.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / deliveryRowsPerPage));
    const currentPage = Math.min(Math.max(1, deliveryPage), totalPages);
    const startIdx = (currentPage - 1) * deliveryRowsPerPage;
    const visibleDeliveries = filteredDeliveries.slice(startIdx, startIdx + deliveryRowsPerPage);

    if (filteredDeliveries.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {deliveries.length === 0
              ? `No ${isIncoming ? 'incoming' : 'outgoing'} deliveries found.`
              : 'No deliveries match your search and filter criteria.'}
          </Typography>
        </Box>
      );
    }

    return (
      <>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Asset</TableCell>
            <TableCell>Serial Number</TableCell>
            <TableCell>{isIncoming ? 'From' : 'To'}</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Delivery Check</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Completed</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleDeliveries.map((delivery) => (
            <TableRow key={delivery.transfer_id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {delivery.asset_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {delivery.asset_type}
                </Typography>
              </TableCell>
              <TableCell>{delivery.serial_number}</TableCell>
              <TableCell>
                {isIncoming ? delivery.from_business_name : delivery.to_business_name}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={delivery.transfer_status.replace('_', ' ')}
                  color={getStatusColor(delivery.transfer_status) as any}
                  icon={getStatusIcon(delivery.transfer_status)}
                />
              </TableCell>
              <TableCell>
                {delivery.delivery_check_id ? (
                  <Stack direction="column" spacing={0.5}>
                    <Typography variant="caption">{delivery.delivery_check_name || 'Delivery Check'}</Typography>
                    <Chip
                      size="small"
                      label={isDeliveryCheckCompleted(delivery) ? 'Completed' : 'Pending'}
                      color={isDeliveryCheckCompleted(delivery) ? 'success' : 'warning'}
                    />
                  </Stack>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No check
                  </Typography>
                )}
              </TableCell>
              <TableCell>{formatDate(delivery.created_at)}</TableCell>
              <TableCell>{formatDate(delivery.completed_at || delivery.delivery_completed_at)}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleViewDetails(delivery)}>
                    Details
                  </Button>
                  {delivery.delivery_check_id && (
                    <Button size="small" variant="contained" onClick={() => handleViewResponses(delivery)}>
                      Responses
                    </Button>
                  )}
                  <DeliveryCheckActionsMenu
                    checkId={delivery.delivery_check_id ?? undefined}
                    transferId={delivery.delivery_source === 'asset_transfer' ? delivery.transfer_id : undefined}
                    outgoingRequestId={delivery.delivery_source === 'outgoing_request' ? delivery.transfer_id : undefined}
                    onActionComplete={fetchDeliveryData}
                  />
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {startIdx + 1} to {Math.min(startIdx + deliveryRowsPerPage, totalCount)} of {totalCount} results
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Per page</InputLabel>
              <Select
                value={deliveryRowsPerPage}
                label="Per page"
                onChange={(e) => {
                  setDeliveryRowsPerPage(Number(e.target.value));
                  setDeliveryPage(1);
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, p) => setDeliveryPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </Paper>
      </>
    );
  };

  const createModal = (
    <CreateDeliveryCheckModal
      open={createModalOpen}
      onClose={() => setCreateModalOpen(false)}
      onCreated={() => fetchDeliveryData({ background: true })}
    />
  );

  if (loading && !refreshing) {
    return (
      <>
        <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        </Box>
        {createModal}
      </>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', pt: 0.5, pb: 3 }}>
        <PageHeader
          title={resolveAdminUiLabel('nav_delivery_checks', adminUiLabels)}
          subtitle="Monitor incoming and outgoing asset deliveries. Track delivery checks and their completion status."
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreateModalOpen(true)}
                sx={primaryButtonSx}
              >
                New Delivery Check
              </Button>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
                sx={primaryButtonSx}
              >
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </Button>
            </Stack>
          }
        />

        {/* Stats KPIs - dashboard style */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Deliveries"
              value={stats ? stats.total_deliveries : <CircularProgress size={24} />}
              icon={<LocalShippingIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Incoming"
              value={stats ? stats.incoming_count : <CircularProgress size={24} />}
              icon={<IncomingIcon />}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Outgoing"
              value={stats ? stats.outgoing_count : <CircularProgress size={24} />}
              icon={<OutgoingIcon />}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="In Transit"
              value={stats ? stats.in_transit_count : <CircularProgress size={24} />}
              icon={<LocalShippingIcon />}
              color="warning"
            />
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Tabs */}
        <Paper sx={{ width: '100%', borderRadius: 1.5, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
            <Tabs
              value={tab}
              onChange={handleTabChange}
              aria-label="delivery checks tabs"
              variant="fullWidth"
              sx={pageTabsSx}
            >
              <Tab 
                label={`Incoming (${filterAndSearchDeliveries(incomingDeliveries).length}${filterAndSearchDeliveries(incomingDeliveries).length !== incomingDeliveries.length ? ` / ${incomingDeliveries.length}` : ''})`} 
                icon={<IncomingIcon />} 
                iconPosition="start" 
              />
              <Tab 
                label={`Outgoing (${filterAndSearchDeliveries(outgoingDeliveries).length}${filterAndSearchDeliveries(outgoingDeliveries).length !== outgoingDeliveries.length ? ` / ${outgoingDeliveries.length}` : ''})`} 
                icon={<OutgoingIcon />} 
                iconPosition="start" 
              />
            </Tabs>

          {/* Search and Filters */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by asset name, serial number, business, check name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Transfer Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Transfer Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    startAdornment={
                      <InputAdornment position="start">
                        <FilterListIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_transit">In Transit</MenuItem>
                    <MenuItem value="delivered">Delivered</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Check Status</InputLabel>
                  <Select
                    value={checkStatusFilter}
                    label="Check Status"
                    onChange={(e) => setCheckStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Checks</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="no_check">No Check</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setCheckStatusFilter('all');
                  }}
                  sx={{ height: '40px' }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TabPanel value={tab} index={0}>
            <Box sx={{ p: 3 }}>{renderDeliveryTable(incomingDeliveries, true)}</Box>
          </TabPanel>

          <TabPanel value={tab} index={1}>
            <Box sx={{ p: 3 }}>{renderDeliveryTable(outgoingDeliveries, false)}</Box>
          </TabPanel>
        </Paper>

        {/* Details Dialog */}
        <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Delivery Details</DialogTitle>
          <DialogContent dividers>
            {selectedDelivery && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Asset
                  </Typography>
                  <Typography>{selectedDelivery.asset_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Serial Number
                  </Typography>
                  <Typography>{selectedDelivery.serial_number}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    From Business
                  </Typography>
                  <Typography>{selectedDelivery.from_business_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    To Business
                  </Typography>
                  <Typography>{selectedDelivery.to_business_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedDelivery.transfer_status.replace('_', ' ')}
                    color={getStatusColor(selectedDelivery.transfer_status) as any}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Transferred By
                  </Typography>
                  <Typography>{selectedDelivery.transferred_by_name}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created
                  </Typography>
                  <Typography>{formatDate(selectedDelivery.created_at)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography>{formatDate(selectedDelivery.completed_at || selectedDelivery.delivery_completed_at)}</Typography>
                </Grid>
                {selectedDelivery.delivery_check_id && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Delivery Check
                      </Typography>
                      <Typography>{selectedDelivery.delivery_check_name || 'Delivery Check'}</Typography>
                      <Box sx={{ mt: 0.5 }}><CheckIdCopy checkId={selectedDelivery.delivery_check_id} label="Check ID" /></Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Check Status
                      </Typography>
                      <Chip
                        size="small"
                        label={isDeliveryCheckCompleted(selectedDelivery) ? 'Completed' : 'Pending'}
                        color={isDeliveryCheckCompleted(selectedDelivery) ? 'success' : 'warning'}
                      />
                    </Grid>
                    {selectedDelivery.delivery_completed_by_name && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Completed By
                        </Typography>
                        <Typography>{selectedDelivery.delivery_completed_by_name}</Typography>
                      </Grid>
                    )}
                  </>
                )}
                {selectedDelivery.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography>{selectedDelivery.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
            {selectedDelivery?.delivery_check_id && (
              <Button variant="contained" onClick={() => handleViewResponses(selectedDelivery)}>
                View Responses
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Responses Dialog */}
        <Dialog open={responsesDialogOpen} onClose={() => setResponsesDialogOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            Delivery Check Responses
            {selectedDelivery && ` - ${selectedDelivery.asset_name}`}
          </DialogTitle>
          <DialogContent dividers>
            {loadingResponses ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : responses.length === 0 ? (
              <Typography color="text.secondary">No responses found for this delivery check.</Typography>
            ) : (
              <Box>
                {/* External driver banner */}
                {responsesExternalDriver?.name && (
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    icon={false}
                  >
                    Completed by external driver: <strong>{responsesExternalDriver.name}</strong>
                    {responsesExternalDriver.email ? ` (${responsesExternalDriver.email})` : ''}
                    {responsesExternalDriver.completedViaLink ? ' via magic link' : ''}
                  </Alert>
                )}
                {/* Linked DVLA licence check */}
                {responsesExternalDriver?.dvlaCheck && (
                  <Alert
                    severity={
                      responsesExternalDriver.dvlaCheck.status === 'completed'
                        ? (responsesExternalDriver.dvlaCheck.licenceValid === false ? 'error' : 'success')
                        : 'warning'
                    }
                    sx={{ mb: 2 }}
                  >
                    <strong>Driving Licence Check:</strong>{' '}
                    {responsesExternalDriver.dvlaCheck.status === 'completed' ? (
                      <>
                        {responsesExternalDriver.dvlaCheck.licenceValid === false
                          ? 'Licence check flagged issues'
                          : 'Licence check passed'}
                        {responsesExternalDriver.dvlaCheck.licenceNumber
                          ? ` — licence ${responsesExternalDriver.dvlaCheck.licenceNumber}`
                          : ''}
                        {responsesExternalDriver.dvlaCheck.completedAt
                          ? ` on ${new Date(responsesExternalDriver.dvlaCheck.completedAt).toLocaleDateString()}`
                          : ''}
                      </>
                    ) : responsesExternalDriver.dvlaCheck.status === 'expired' ? (
                      'Licence check request expired — driver did not submit'
                    ) : (
                      'Awaiting driver to submit their driving licence number'
                    )}
                  </Alert>
                )}
                {responses.map((item, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Question
                        </Typography>
                        <Typography>{item.question?.question_text || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Answer
                        </Typography>
                        {renderDeliveryResponseAnswer(item)}
                      </Grid>
                      {item.response?.additional_text && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Additional Notes
                          </Typography>
                          <Typography>{item.response.additional_text}</Typography>
                        </Grid>
                      )}
                      {item.images && item.images.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Images ({item.images.length})
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {item.images.map((img: any, imgIndex: number) => (
                              <img
                                key={imgIndex}
                                src={img.image_path}
                                alt={`Response ${imgIndex + 1}`}
                                style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', objectFit: 'contain' }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      {item.response && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Answered by: {item.response.user_name || responsesExternalDriver?.name || 'Unknown'} on {new Date(item.response.created_at).toLocaleString()}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResponsesDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {createModal}
    </Box>
  );
};

export default DeliveryChecks;

